/* The extension and the website, working as one.

   The PathsAbroad extension runs a small script (apps/extension/src/site.ts) on PathsAbroad's own pages, and those
   pages talk to it with window.postMessage: same window, same origin, a channel name on every message. The site uses
   it to:
   - see whether the extension is installed and connected (ping → status);
   - connect it to the student who is signed in, with nothing to type: the page asks the API for a one-use pairing
     code (POST /api/extension/pair) and hands it over (connect). It is the same code students used to type;
   - disconnect it when the student signs out, or when the site finds nobody signed in on this browser (signout).
   The extension answers with its status: whether it is connected, until when, and a link id (the first 16 hex
   characters of the SHA-256 of its token). GET /api/extension/link checks the link id against the signed-in
   student's own connections, so a browser shared by two students never fills one student's answers into the other's
   application. The token itself never leaves the extension's background script. */
import { isLang, type Lang } from './contract';

export const EXT_CHANNEL = 'pathsabroad-extension';
/* Reconnect when less than this is left of the extension's 12-hour connection, so a student who uses the site never
   finds the extension disconnected when they get to a portal. */
export const EXT_REFRESH_MS = 4 * 3600_000;
export const isLinkId = (x: unknown): x is string => typeof x === 'string' && /^[0-9a-f]{16}$/.test(x);

export type ExtStatus = { version: string; paired: boolean; link: string | null; expires_at: string | null };

/* page → extension. id: a fresh request id, which the answer carries back as `re` */
export type SiteToExt =
  | { channel: typeof EXT_CHANNEL; from: 'site'; type: 'ping'; id: string; lang?: Lang }
  | { channel: typeof EXT_CHANNEL; from: 'site'; type: 'connect'; id: string; code: string; lang?: Lang }
  | { channel: typeof EXT_CHANNEL; from: 'site'; type: 'signout'; id: string };

/* extension → page: the answer to a request (re = its id), or '' when the extension says hello unasked */
export type ExtToSite = { channel: typeof EXT_CHANNEL; from: 'ext'; type: 'status'; re: string; ok: boolean; error?: string; status: ExtStatus };

const isObj = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x);
const isReqId = (x: unknown): x is string => typeof x === 'string' && /^[A-Za-z0-9-]{1,64}$/.test(x);
const PAIR_CODE = /^[A-Z0-9]{4}-?[A-Z0-9]{4}$/;

export function parseExtStatus(x: unknown): ExtStatus | null {
  if (!isObj(x) || typeof x.paired !== 'boolean' || typeof x.version !== 'string' || !/^[0-9][0-9.]{0,19}$/.test(x.version)) return null;
  const link = x.link ?? null, exp = x.expires_at ?? null;
  if (link !== null && !isLinkId(link)) return null;
  if (exp !== null && (typeof exp !== 'string' || exp.length > 40 || Number.isNaN(Date.parse(exp)))) return null;
  if (x.paired !== (link !== null && exp !== null)) return null;   // connected means a link id and an end time
  return { version: x.version, paired: x.paired, link, expires_at: exp };
}

export function parseSiteToExt(x: unknown): SiteToExt | null {
  if (!isObj(x) || x.channel !== EXT_CHANNEL || x.from !== 'site' || !isReqId(x.id)) return null;
  if (x.lang !== undefined && !isLang(x.lang)) return null;
  const lang = x.lang === undefined ? {} : { lang: x.lang as Lang };
  switch (x.type) {
    case 'ping': return { channel: EXT_CHANNEL, from: 'site', type: 'ping', id: x.id, ...lang };
    case 'connect':
      if (typeof x.code !== 'string' || !PAIR_CODE.test(x.code)) return null;
      return { channel: EXT_CHANNEL, from: 'site', type: 'connect', id: x.id, code: x.code, ...lang };
    case 'signout': return { channel: EXT_CHANNEL, from: 'site', type: 'signout', id: x.id };
    default: return null;
  }
}

export function parseExtToSite(x: unknown): ExtToSite | null {
  if (!isObj(x) || x.channel !== EXT_CHANNEL || x.from !== 'ext' || x.type !== 'status' || typeof x.ok !== 'boolean') return null;
  if (!(x.re === '' || isReqId(x.re))) return null;
  if (x.error !== undefined && (typeof x.error !== 'string' || !/^[a-z_]{1,32}$/.test(x.error))) return null;
  const status = parseExtStatus(x.status);
  if (!status) return null;
  return { channel: EXT_CHANNEL, from: 'ext', type: 'status', re: x.re as string, ok: x.ok, ...(typeof x.error === 'string' ? { error: x.error } : {}), status };
}

/* ---------- the site's side ---------- */
type MessageWindow = {
  addEventListener(type: 'message', fn: (e: MessageEvent) => void): void;
  removeEventListener(type: 'message', fn: (e: MessageEvent) => void): void;
  postMessage(message: unknown, targetOrigin: string): void;
  location: { origin: string };
};

export type ExtensionLink = {
  /* the extension's last known status: null until it answers, and for good when it isn't installed */
  readonly status: ExtStatus | null;
  subscribe(fn: (s: ExtStatus | null) => void): () => void;
  /* null: no extension answered in time */
  ping(lang?: Lang, timeoutMs?: number): Promise<ExtStatus | null>;
  connect(code: string, lang?: Lang): Promise<ExtToSite | null>;
  signout(): Promise<ExtToSite | null>;
  close(): void;
};

const newId = (): string => {
  try { return globalThis.crypto.randomUUID(); } catch { return Math.random().toString(36).slice(2) + Date.now().toString(36); }
};

export function openExtensionLink(win: MessageWindow): ExtensionLink {
  const origin = win.location.origin;
  const listeners = new Set<(s: ExtStatus | null) => void>();
  const waiting = new Map<string, (m: ExtToSite) => void>();
  let status: ExtStatus | null = null;
  const onMessage = (e: MessageEvent) => {
    if (e.source !== (win as unknown) || e.origin !== origin) return;
    const m = parseExtToSite(e.data);
    if (!m) return;
    status = m.status;
    for (const fn of listeners) fn(status);
    const done = m.re ? waiting.get(m.re) : undefined;
    if (done) { waiting.delete(m.re); done(m); }
  };
  win.addEventListener('message', onMessage);
  const ask = (msg: SiteToExt, timeoutMs: number) => new Promise<ExtToSite | null>(resolve => {
    const timer = setTimeout(() => { waiting.delete(msg.id); resolve(null); }, timeoutMs);
    waiting.set(msg.id, m => { clearTimeout(timer); resolve(m); });
    win.postMessage(msg, origin);
  });
  const withLang = (lang?: Lang) => (lang ? { lang } : {});
  return {
    get status() { return status; },
    subscribe(fn) { listeners.add(fn); return () => { listeners.delete(fn); }; },
    async ping(lang, timeoutMs = 1500) {
      const r = await ask({ channel: EXT_CHANNEL, from: 'site', type: 'ping', id: newId(), ...withLang(lang) }, timeoutMs);
      return r ? r.status : null;
    },
    connect(code, lang) { return ask({ channel: EXT_CHANNEL, from: 'site', type: 'connect', id: newId(), code, ...withLang(lang) }, 25_000); },
    signout() { return ask({ channel: EXT_CHANNEL, from: 'site', type: 'signout', id: newId() }, 10_000); },
    close() { win.removeEventListener('message', onMessage); listeners.clear(); waiting.clear(); },
  };
}

export type ExtSyncState = 'missing' | 'signed_out' | 'no_plan' | 'connected' | 'error';
export type ExtSyncResult = { state: ExtSyncState; error?: string };
export type ExtSyncDeps = {
  signedIn: boolean;
  apply: boolean;                                    // the extension comes with the Apply plan
  lang?: Lang;
  mintCode: () => Promise<string>;                   // POST /api/extension/pair → its code
  isLinked: (link: string) => Promise<boolean>;      // GET /api/extension/link?id=
  lock?: <T>(fn: () => Promise<T>) => Promise<T>;    // one tab at a time (navigator.locks)
  now?: () => number;
};

const errorCode = (e: unknown): string => {
  const c = isObj(e) && typeof e.code === 'string' ? e.code : '';
  return /^[a-z_]{1,32}$/.test(c) ? c : 'server';
};

/* Make the extension match the site: connected to the student who is signed in here, and to nobody when nobody is.
   Called when a page loads and when the session changes. It never connects a student without the Apply plan, and
   connects again only when the extension isn't connected to this student or its connection is nearly over. */
export async function syncExtension(link: ExtensionLink, d: ExtSyncDeps): Promise<ExtSyncResult> {
  const now = d.now ?? Date.now;
  const first = await link.ping(d.lang);
  if (!first) return { state: 'missing' };
  if (!d.signedIn) {
    if (first.paired) await link.signout();
    return { state: 'signed_out' };
  }
  if (!d.apply) return { state: 'no_plan' };
  const run = d.lock ?? (<T>(fn: () => Promise<T>) => fn());
  return run(async (): Promise<ExtSyncResult> => {
    const st = (await link.ping(d.lang)) ?? first;    // another tab may have connected it a moment ago
    if (st.paired && st.link && st.expires_at && Date.parse(st.expires_at) - now() > EXT_REFRESH_MS) {
      let linked: boolean;
      try { linked = await d.isLinked(st.link); } catch (e) { return { state: 'error', error: errorCode(e) }; }
      if (linked) return { state: 'connected' };
    }
    let code: string;
    try { code = await d.mintCode(); } catch (e) { return { state: 'error', error: errorCode(e) }; }
    const r = await link.connect(code, d.lang);
    if (!r) return { state: 'error', error: 'no_answer' };
    return r.ok ? { state: 'connected' } : { state: 'error', error: r.error ?? 'server' };
  });
}
